import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import React, { FC, useMemo } from 'react';
import { isNil } from 'lodash-es';
import {
  dash2LegendIcon,
  dash3LegendIcon,
  DataType,
  getEndLabel,
  getYAixs,
  pcsColorList,
  socColorList,
} from '../../const';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiV2MicrogridOverviewRunningDataPost } from '@maxtropy/intelligent-dispath-apis-v2';

export interface ISOCMonitor {
  bsaId?: number;
  showPcs?: boolean;
}

const SOCMonitor: FC<ISOCMonitor> = props => {
  const { bsaId, showPcs = true } = props;
  const theme = useTheme();

  // SOC
  const { data: socDatas, loading } = useRequest(
    async () => {
      const res = await apiV2MicrogridOverviewRunningDataPost({
        bsaId: bsaId,
        bsaType: 1,
        tabType: '2',
        time: dayjs().startOf('day').valueOf(),
        timeGranularity: 2,
      });
      return res?.list ?? [];
    },
    {
      ready: !!bsaId,
      refreshDeps: [bsaId],
    }
  );

  const echartsOption = useMemo(() => {
    const socData = socDatas?.filter(item => item.dataType === DataType.SOC);
    const pcsData = socDatas?.filter(item => item.dataType === DataType.PCS);

    const option = {
      backgroundColor: 'transparent',
      grid: {
        left: (socData ?? []).length > 0 ? 10 : 24,
        right: (pcsData ?? []).length > 0 ? 10 : 24,
        top: 55,
        bottom: 10,
        containLabel: true,
      },
      xAxis: {
        type: 'time',
      },
      yAxis: showPcs ? [getYAixs('SOC(%)'), getYAixs('PCS(kW)')] : [getYAixs('SOC(%)')],
      tooltip: {
        formatter(items: any[]) {
          const { axisValueLabel } = items[0];
          let str: string = axisValueLabel;

          items.forEach(item => {
            const { seriesName, data, marker } = item;
            const value = !isNil(data[1]) ? `${data[1].toFixed(4)}` : '--';
            const unit = data[2];
            str += `<div style="display:flex;margin-top:4px;justify-content:space-between;gap:30px;"><span>${marker}${seriesName}</span> <span>${value}${unit}</span></div>`;
          });

          return str;
        },
      },
      legend: {
        show: true,
        type: 'scroll',
        right: 20,
        textStyle: {
          fontSize: '12px',
        },
        itemWidth: 16,
        itemHeight: 4,
        data: [],
      },
      // dataZoom: [{ start: 0, end: 100 }],
      series: [],
    };

    let legendData = option.legend.data as any[];
    let series = option.series as any[];
    // soc
    socData?.forEach((item, index) => {
      legendData.push({
        name: item.deviceName,
        icon: dash3LegendIcon,
        itemStyle: {
          color: socColorList[index % socColorList.length],
        },
      });
      series.push({
        name: item.deviceName,
        type: 'line',
        yAxisIndex: 0,
        showSymbol: false,
        itemStyle: {
          color: socColorList[index % socColorList.length],
        },
        lineStyle: {
          type: [2, 2],
        },

        data:
          item.bsaDataList?.map(dataItem => {
            return [dataItem.time, dataItem.value, '%'];
          }) ?? [],
        // ...getEndLabel(
        //   item.bsaDataList?.map(dataItem => {
        //     return [dataItem.time, dataItem.value];
        //   }) as any[],
        //   '%'
        // ),
      });
    });
    // pcs
    if (showPcs) {
      pcsData?.forEach((item, index) => {
        legendData.push({
          name: item.deviceName,
          icon: dash2LegendIcon,
          itemStyle: {
            color: pcsColorList[index % pcsColorList.length],
          },
        });
        series.push({
          name: item.deviceName,

          yAxisIndex: 1,
          type: 'line',
          showSymbol: false,
          itemStyle: {
            color: pcsColorList[index % pcsColorList.length],
          },
          lineStyle: {
            type: [6, 6],
          },
          smooth: true,
          data:
            item.bsaDataList?.map(dataItem => {
              return [dataItem.time, dataItem.value, 'kW'];
            }) ?? [],
          // ...getEndLabel(
          //   item.bsaDataList?.map(dataItem => {
          //     return [dataItem.time, dataItem.value];
          //   }) as any[]
          // ),
        });
      });
    }

    return option;
  }, [showPcs, socDatas]);

  const notEmpty = socDatas?.some(item => item.bsaDataList?.length);

  return (
    <>
      <EchartsWithTheme
        loading={loading}
        option={echartsOption}
        theme={theme}
        style={{ height: '100%' }}
        empty={{
          emptyNode: <Empty style={{ marginTop: 30 }} />,
          isShowEmpty: !notEmpty,
        }}
      />
    </>
  );
};

export default SOCMonitor;
